/**
 * 节流指令
 * 用于限制事件触发频率
 * @author 系统
 * @date 2024-01-01
 */

import type { Directive, DirectiveBinding } from 'vue'

interface ThrottleElement extends HTMLElement {
  _throttleTimer?: number
  _throttleHandler?: (event: Event) => void
  _lastExecTime?: number
}

/**
 * 创建节流函数
 * @param func 要节流的函数
 * @param delay 延迟时间（毫秒）
 * @returns 节流后的函数
 */
const createThrottle = (func: Function, delay: number) => {
  let lastExecTime = 0
  let timer: number | null = null

  return function (this: any, ...args: any[]) {
    const currentTime = Date.now()

    if (currentTime - lastExecTime > delay) {
      // 如果距离上次执行时间超过延迟时间，立即执行
      func.apply(this, args)
      lastExecTime = currentTime
    } else {
      // 否则设置定时器，在剩余时间后执行
      if (timer) {
        clearTimeout(timer)
      }

      timer = window.setTimeout(() => {
        func.apply(this, args)
        lastExecTime = Date.now()
        timer = null
      }, delay - (currentTime - lastExecTime))
    }
  }
}

/**
 * 节流指令实现
 */
export const throttleDirective: Directive = {
  /**
   * 元素挂载时
   */
  mounted(el: ThrottleElement, binding: DirectiveBinding) {
    const { value, arg, modifiers } = binding

    if (typeof value !== 'function') {
      console.warn('v-throttle 指令的值必须是一个函数')
      return
    }

    // 获取延迟时间，默认1000ms
    const delay = parseInt(arg || '1000')

    // 获取事件类型，默认click
    const eventType = modifiers.scroll ? 'scroll' :
      modifiers.resize ? 'resize' :
        modifiers.mousemove ? 'mousemove' :
          modifiers.input ? 'input' :
            modifiers.change ? 'change' : 'click'

    // 创建节流处理函数
    const throttledHandler = createThrottle(value, delay)

    // 绑定事件
    el.addEventListener(eventType, throttledHandler)

    // 保存处理函数引用，用于清理
    el._throttleHandler = throttledHandler
    el._lastExecTime = 0
  },

  /**
   * 指令值更新时
   */
  updated(el: ThrottleElement, binding: DirectiveBinding) {
    const { value, arg, modifiers, oldValue } = binding

    // 如果函数没有变化，不需要重新绑定
    if (value === oldValue) {
      return
    }

    if (typeof value !== 'function') {
      console.warn('v-throttle 指令的值必须是一个函数')
      return
    }

    // 移除旧的事件监听器
    if (el._throttleHandler) {
      const eventType = modifiers.scroll ? 'scroll' :
        modifiers.resize ? 'resize' :
          modifiers.mousemove ? 'mousemove' :
            modifiers.input ? 'input' :
              modifiers.change ? 'change' : 'click'
      el.removeEventListener(eventType, el._throttleHandler)
    }

    // 获取延迟时间，默认1000ms
    const delay = parseInt(arg || '1000')

    // 获取事件类型，默认click
    const eventType = modifiers.scroll ? 'scroll' :
      modifiers.resize ? 'resize' :
        modifiers.mousemove ? 'mousemove' :
          modifiers.input ? 'input' :
            modifiers.change ? 'change' : 'click'

    // 创建新的节流处理函数
    const throttledHandler = createThrottle(newValue, delay)

    // 绑定新的事件
    el.addEventListener(eventType, throttledHandler)

    // 更新处理函数引用
    el._throttleHandler = throttledHandler
    el._lastExecTime = 0
  },

  /**
   * 元素卸载时
   */
  unmounted(el: ThrottleElement) {
    if (el._throttleHandler) {
      // 移除事件监听器
      const events = ['click', 'scroll', 'resize', 'mousemove', 'input', 'change']
      events.forEach(eventType => {
        el.removeEventListener(eventType, el._throttleHandler!)
      })

      // 清理引用
      delete el._throttleHandler
      delete el._lastExecTime
    }

    // 清理定时器
    if (el._throttleTimer) {
      clearTimeout(el._throttleTimer)
      delete el._throttleTimer
    }
  }
}
